import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2,
  Globe,
  ListFilter,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { adminPlatformSettingService } from "@/services/admin-platform-setting.service";
import {
  SETTING_TYPES,
  SETTING_TYPE_LABELS,
  SENSITIVE_TYPES,
  isFeatureFlagType,
  type PlatformSettingListDto,
} from "@/types/platform-setting";
import {
  platformSettingFormSchema,
  type PlatformSettingFormData,
} from "@/schemas/platform-setting";

function ScopeBadge({
  tenantId,
  region,
}: {
  tenantId?: string;
  region?: string;
}) {
  if (tenantId && region)
    return (
      <Badge variant="outline" className="gap-1">
        <Building2 className="h-3 w-3" /> Tenant + Region
      </Badge>
    );
  if (tenantId)
    return (
      <Badge variant="outline" className="gap-1">
        <Building2 className="h-3 w-3" /> Tenant
      </Badge>
    );
  if (region)
    return (
      <Badge variant="outline" className="gap-1">
        <MapPin className="h-3 w-3" /> Region
      </Badge>
    );
  return (
    <Badge variant="secondary" className="gap-1">
      <Globe className="h-3 w-3" /> Global
    </Badge>
  );
}

/** Every list screen commits its search box after the same delay. */
const SEARCH_DEBOUNCE_MS = 750;

const PAGE_SIZES = [10, 20, 50, 100];

/** Widths keyed by column id, so adding a column without a width is a compile error. */
const COLUMN_WIDTHS: Record<
  "key" | "type" | "scope" | "value" | "status" | "actions",
  string
> = {
  // A snake_case key over a two-line description.
  key: "w-96",
  // Fits the longest type label ("Agreement — VM Creation Commitment") on one line.
  type: "w-72",
  // Fits the widest scope badge, "Tenant + Region".
  scope: "w-40",
  // Truncated monospace JSON, so it takes the room the others leave.
  value: "w-120",
  status: "w-28",
  actions: "w-24",
};

export function GeneralSettingsTab() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string>("All");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PlatformSettingListDto | null>(
    null,
  );

  // The box commits after a pause; until it does, the table still shows the previous result,
  // which is why the input carries its own spinner below.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data: response,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "admin",
      "platform-settings",
      { typeFilter, page, pageSize, search },
    ],
    queryFn: () =>
      adminPlatformSettingService.getSettings({
        type: typeFilter === "All" ? undefined : typeFilter,
        // The backend matches `key` with Contains, so this is a substring search over the
        // setting's identifier — the only field on this screen that names a row exactly.
        key: search || undefined,
        page,
        limit: pageSize,
      }),
    // Keeps the previous page on screen while the next one loads, so a page change or a
    // committed search never blanks the table.
    placeholderData: keepPreviousData,
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      type: string;
      key: string;
      value: string;
      description?: string;
      tenantId?: string;
      region?: string;
    }) => adminPlatformSettingService.createSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success("Setting created");
      setIsFormOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { value?: string; description?: string; isActive?: boolean };
    }) => adminPlatformSettingService.updateSetting(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success("Setting updated");
      setIsFormOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const items = response?.data ?? [];
  const totalItems = response?.pagination?.totalItems ?? 0;
  const totalPages = response?.pagination?.totalPages ?? 1;
  const currentPage = response?.pagination?.currentPage ?? page;

  // Skeleton only while there is nothing to show. Once rows are on screen a refetch shows the
  // pill instead, so the table never disappears under the operator.
  const isReloading = isFetching && items.length === 0;
  const isSearchPending = searchInput.trim() !== search;
  const hasActiveFilters = typeFilter !== "All" || search !== "";

  const resetAndReload = () => {
    setTypeFilter("All");
    setSearchInput("");
    setSearch("");
    setPage(1);
    void refetch();
  };

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center pt-4">
            <span className="flex items-center gap-2 rounded-full border bg-background/95 px-3 py-1 text-xs shadow-sm">
              <Loader2 className="h-3 w-3 animate-spin" />
              Updating…
            </span>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="relative w-full sm:w-[360px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by setting key"
                aria-label="Search by setting key"
                className="h-9 pl-10 pr-9"
              />
              {isSearchPending && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>

            <Button
              size="sm"
              className="h-8 min-h-8 shrink-0 gap-2"
              onClick={() => {
                setEditingItem(null);
                setIsFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Add Setting
            </Button>

            {hasActiveFilters && (
              <Badge
                variant="secondary"
                className="h-8 min-h-8 shrink-0 gap-1 px-2"
              >
                <ListFilter className="h-3 w-3" />
                Filtered
              </Badge>
            )}

            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Select
                value={typeFilter}
                onValueChange={(value) => {
                  setTypeFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger
                  className="h-8 min-h-8 w-[220px]"
                  aria-label="Filter settings by type"
                >
                  <SelectValue placeholder="All settings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All settings</SelectItem>
                  {SETTING_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {SETTING_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {/* With rows in hand the failure is a banner, not a takeover: a full-screen retry card
              would throw the filter bar away too. */}
          {error && items.length > 0 && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
              <span>Could not refresh the list. Showing the last result.</span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 min-h-8"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          {error && items.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
              <p className="text-sm text-muted-foreground">
                Failed to load platform settings.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            </div>
          ) : isReloading ? (
            <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="h-9 animate-pulse rounded bg-muted/60"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <p className="text-sm font-medium">No settings found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {hasActiveFilters
                  ? "No setting matches the current filters."
                  : "Add a setting to get started."}
              </p>
            </div>
          ) : (
            // The scroll lives here, not on the wrapper `Table` renders: that one is as tall as
            // the whole table, so its bar would sit below the last row, off-screen.
            <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
              {/* Dividers stop one column short of the end: the sticky Actions column draws its
                  own left line, and a border on Status beside it would double it to 2px. */}
              <Table className="table-fixed border-collapse [&_td:nth-last-child(n+3)]:border-r [&_th:nth-last-child(n+3)]:border-r">
                <colgroup>
                  <col className={COLUMN_WIDTHS.key} />
                  <col className={COLUMN_WIDTHS.type} />
                  <col className={COLUMN_WIDTHS.scope} />
                  <col className={COLUMN_WIDTHS.value} />
                  <col className={COLUMN_WIDTHS.status} />
                  <col className={COLUMN_WIDTHS.actions} />
                </colgroup>
                {/* z-30, above the sticky Actions cells (z-10) — at an equal z-index a later body
                    cell paints over the header's Actions cell while scrolling. The inset shadow
                    stands in for the row's bottom border, which a collapsed-border table leaves
                    behind when the header sticks. */}
                <TableHeader className="sticky top-0 z-30 bg-card shadow-[inset_0_-1px_0_0_var(--border)]">
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_-1px_0_0_var(--border)]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="align-top">
                        <div className="font-medium wrap-break-word">
                          {item.key}
                        </div>
                        {item.description && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {item.description}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge variant="outline">
                          {SETTING_TYPE_LABELS[
                            item.type as keyof typeof SETTING_TYPE_LABELS
                          ] || item.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-top">
                        <ScopeBadge
                          tenantId={item.tenantId}
                          region={item.region}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <span className="block truncate rounded bg-muted px-2 py-1 font-mono text-xs">
                          {SENSITIVE_TYPES.has(item.type)
                            ? "••••••••"
                            : item.value}
                        </span>
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge
                          variant={item.isActive ? "default" : "outline"}
                          className={
                            item.isActive
                              ? "bg-emerald-600 text-white hover:bg-emerald-600"
                              : undefined
                          }
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="sticky right-0 z-10 bg-card text-center align-top shadow-[inset_1px_0_0_0_var(--border)]">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Edit ${item.key}`}
                          onClick={() => {
                            setEditingItem(item);
                            setIsFormOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Outside the branches above and gated only on the skeleton, so a zero-result search
              keeps its pager and page-size picker. */}
          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </span>
              <div className="flex items-center gap-2">
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    className="h-8 min-h-8 w-[120px]"
                    aria-label="Rows per page"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} / page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 min-h-8"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 min-h-8"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  onClick={resetAndReload}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <SettingFormDialog
        key={editingItem?.id ?? "new"}
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        item={editingItem}
        onCreate={(data) => createMutation.mutate(data)}
        onUpdate={(id, data) => updateMutation.mutate({ id, data })}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}

interface SettingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PlatformSettingListDto | null;
  onCreate: (data: {
    type: string;
    key: string;
    value: string;
    description?: string;
    tenantId?: string;
    region?: string;
  }) => void;
  onUpdate: (
    id: string,
    data: { value?: string; description?: string; isActive?: boolean },
  ) => void;
  isSubmitting: boolean;
}

function SettingFormDialog({
  open,
  onOpenChange,
  item,
  onCreate,
  onUpdate,
  isSubmitting,
}: SettingFormDialogProps) {
  const isEdit = !!item;
  // Feature-flag types are edited as a boolean toggle (Value "true"/"false")
  // with IsActive forced true — see handleSubmit and the Value field below.
  const isFlagType = !!item && isFeatureFlagType(item.type);

  const form = useForm<PlatformSettingFormData>({
    resolver: zodResolver(platformSettingFormSchema),
    defaultValues: isEdit
      ? {
          type: item.type,
          key: item.key,
          value: item.value,
          description: item.description ?? "",
          tenantId: item.tenantId ?? "",
          region: item.region ?? "",
          isActive: item.isActive,
        }
      : {
          type: "VatRate",
          key: "",
          value: "",
          description: "",
          tenantId: "",
          region: "",
          isActive: true,
        },
  });

  const handleSubmit = (data: PlatformSettingFormData) => {
    if (isEdit) {
      // P0 defense: feature flags must never be saved with IsActive=false. The
      // toggle state lives entirely in Value ("true"/"false"); IsActive is
      // forced true so an inactive row can never silently re-enable a flag.
      const flagType = isFeatureFlagType(item.type);
      onUpdate(item.id, {
        value: data.value,
        description: data.description || undefined,
        isActive: flagType ? true : data.isActive,
      });
    } else {
      if (!data.type) {
        form.setError("type", { message: "Type is required" });
        return;
      }
      if (!data.key) {
        form.setError("key", { message: "Key is required" });
        return;
      }
      onCreate({
        type: data.type,
        key: data.key,
        value: data.value,
        description: data.description || undefined,
        tenantId: data.tenantId || undefined,
        region: data.region || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Setting" : "Add Setting"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className={DIALOG_FORM_CLASSES}
        >
          <DialogBody className="space-y-4">
            <DialogDescription>
              {isEdit
                ? "Update this configuration value"
                : "Create a new platform configuration setting"}
            </DialogDescription>
            {!isEdit && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={form.watch("type")}
                    onValueChange={(val) => form.setValue("type", val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SETTING_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {SETTING_TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.type && (
                    <p className="text-sm text-destructive-ink">
                      {form.formState.errors.type.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Key</Label>
                  <Input
                    {...form.register("key")}
                    placeholder="e.g., vat_rate_default"
                  />
                  {form.formState.errors.key && (
                    <p className="text-sm text-destructive-ink">
                      {form.formState.errors.key.message}
                    </p>
                  )}
                </div>
              </>
            )}
            {isEdit && isFlagType ? (
              <div className="space-y-2">
                <Label>Value</Label>
                <Controller
                  control={form.control}
                  name="value"
                  render={({ field }) => (
                    <div className="flex items-center gap-3">
                      <Switch
                        id="editFlagValue"
                        checked={field.value === "true"}
                        onCheckedChange={(checked) =>
                          field.onChange(checked ? "true" : "false")
                        }
                      />
                      <Label htmlFor="editFlagValue">
                        {field.value === "true" ? "Enabled" : "Disabled"}
                      </Label>
                    </div>
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  Feature flags use a boolean value. Active is always on; toggle
                  here to enable or disable the feature.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Value</Label>
                <Textarea
                  {...form.register("value")}
                  placeholder="Setting value"
                  rows={2}
                />
                {form.formState.errors.value && (
                  <p className="text-sm text-destructive-ink">
                    {form.formState.errors.value.message}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                {...form.register("description")}
                placeholder="Optional description"
              />
            </div>
            {isEdit && !isFlagType && (
              <Controller
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch
                      id="editIsActive"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label htmlFor="editIsActive">Active</Label>
                  </div>
                )}
              />
            )}
            {!isEdit && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tenant ID (optional)</Label>
                  <Input
                    {...form.register("tenantId")}
                    placeholder="Global if empty"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region (optional)</Label>
                  <Input
                    {...form.register("region")}
                    placeholder="All regions if empty"
                  />
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
